import * as THREE from 'three';
import gsap from 'gsap';
import GameMover from './GameMover.js';


class Stairs {
    // --- Configuration object for easy settings management ---
    config = {
        ar: {
            startPosition: { x: 0, y: -0.8, z: -2.5 },
            // controllerSensitivity is no longer needed with pointer controls
            moverStartPosition: new THREE.Vector3(0, 5, -10),
            moverStartScale: new THREE.Vector3(5, 0.8, 0.2), // Scale for the game mover in AR
        },
        // --- VR-specific configuration ---
        vr: {
            // Controls the scale of the game world in VR.
            // 1.0 = normal size.
            // 0.5 = half size (tabletop feel).
            // 2.0 = double size (player feels small).
            scale: 0.2,
        },
        ui: {
            buttonSize: 1,
            iconPaths: {
                play: 'assets/icons/play.png',
                restart: 'assets/icons/restart.png',
                passthroughShow: 'assets/icons/passthrough_show.png',
                passthroughHide: 'assets/icons/passthrough_hide.png',
                sfxOn: 'assets/icons/sfx_on.png',
                sfxOff: 'assets/icons/sfx_off.png',
            }
        }
    };

    endGameDelayMs = 500;
    lastTime = 0; // Last timestamp of animation
    stack = []; // Platforms
    boxHeight = 0.4; // Height of each layer
    boxDepth = 1.2; // Original width and height of a box
    boxWidth = 5;

    obstacleWidth = 0.625;
    obstacleDepth = 1.5;
    obstacleHeight = 0.6;

    gameEnded;

    bonusTex;

    camera;
    scene;
    renderer; // ThreeJS globals

    // --- Desktop/Mobile Input ---
    screenInputPosX;
    screemInputLastPosX
    screenInputDeltaX;
    inputMultiplier = 4.2;
    mouseDown = false;

    // --- Game State ---
    playerBall;
    playerBallTimeline;
    playerRadius = 0.4;
    gameSpeed;
    gameSpeedIncrease = 0.02;
    boxCounter;
    jumpCounter;
    bonusCounter;

    gameContainer;
    ui2d;

    // --- Particle Systems ---
    explosionParticleSystem;
    explosionGeometry;
    explosionParticlesCount = 100;
    movementSpeed = 0.4;
    explosionParticlesDirs = [];
    isExploding = false;
    starParticleSystem;
    starGeometry;
    starParticlesCount = 100;

    // --- XR & 3D UI Properties ---
    vrControlPlane; // NEW: Invisible plane for VR pointer controls
    gameGroup; // Group for all game objects to move them together
    uiGroup; // Group for 3D UI elements
    score3d; // NEW: Holds the 3D score object
    controllers = [];
    raycaster;
    mainActionButton;
    passthroughButton;
    sfxButton;
    isPassthroughActive = false;
    isSfxMuted = false;
    textureLoader = new THREE.TextureLoader();
    audioListener;
    sounds = {}; // To hold loaded sounds
    gameMover;

    constructor({ gameContainer, ui2d, leaderboard }) { // ARButton is no longer needed here
        this.gameContainer = gameContainer;
        this.leaderboard = leaderboard || null;
        this.ui2d = {
            container: ui2d,
            score: ui2d.querySelector('#score-2d'),
            start: ui2d.querySelector('#start-btn-2d'),
            restart: ui2d.querySelector('#restart-btn-2d'),
        };

        const pathToParticle = `assets/ShFX_Circle_Glow.png`;
        this.bonusTex = this.textureLoader.load(pathToParticle);

        this.init();
        this.onResize();
    }

    init = () => { // ARButton is no longer passed here
        this.sceneWidth = window.innerWidth;
        this.sceneHeight = window.innerHeight;

        this.gameEnded = true;
        this.lastTime = 0;
        this.boxCounter = 0;
        this.jumpCounter = 0;
        this.bonusCounter = 0;
        this.stack = [];

        this.camera = new THREE.PerspectiveCamera(80, this.sceneWidth / this.sceneHeight, 0.1, 1000);
        this.camera.position.set(0, 4, 5);
        this.camera.lookAt(0, 0, -5);

        this.audioListener = new THREE.AudioListener();
        this.camera.add(this.audioListener);
        // this.loadSounds(); // You can uncomment this when you have sound files

        this.scene = new THREE.Scene();
        this.scene.fog = new THREE.Fog(0x000000, 5, 25);

        this.gameGroup = new THREE.Group();
        this.scene.add(this.gameGroup);

        // --- NEW: Create the invisible plane for VR pointer controls ---
        const planeGeometry = new THREE.PlaneGeometry(100, 100); // Make it large enough
        const planeMaterial = new THREE.MeshBasicMaterial({ visible: false, side: THREE.DoubleSide });
        this.vrControlPlane = new THREE.Mesh(planeGeometry, planeMaterial);
        //this.vrControlPlane.rotation.y = -Math.PI / 2; // Lay it flat, horizontally
        this.vrControlPlane.position.y = 0; // Position it at the game's "floor" level
        this.gameGroup.add(this.vrControlPlane);

        const starConfig = {
            particleCount: 200,
            radius: 40,
            color: (i, count) => new THREE.Color().setHSL(i / count, 0.2, 0.5),
            size: () => 0.01,
            position: () => new THREE.Vector3((Math.random() * 2 - 1) * 40, (Math.random()) * 20, -20 - Math.random() * 20)
        };
        this.starParticleSystem = this.createParticleSystem(starConfig, this.textureLoader.load(`assets/ShFX_Circle_Glow.png`));
        this.starGeometry = this.starParticleSystem.geometry;
        this.starParticlesCount = starConfig.particleCount;
        this.scene.add(this.starParticleSystem);

        const explosionConfig = {
            particleCount: 100,
            color: new THREE.Color().setHSL(1, 0.2, 0.5),
            size: () => Math.random() * 0.3,
            particleInit: (pos, dir) => {
                this.explosionParticlesDirs.push(dir);
            }
        };
        this.explosionParticleSystem = this.createParticleSystem(explosionConfig, this.bonusTex);
        this.explosionGeometry = this.explosionParticleSystem.geometry;
        this.explosionParticlesCount = explosionConfig.particleCount;
        this.explosionParticleSystem.visible = false;
        this.gameGroup.add(this.explosionParticleSystem);

        const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
        this.scene.add(ambientLight);

        const dirLight = new THREE.DirectionalLight(0xffffff, 0.6);
        dirLight.position.set(0.1, 1.4, 0);
        dirLight.castShadow = true;
        dirLight.shadow.radius = 2;
        dirLight.shadow.mapSize.width = 1024;
        dirLight.shadow.mapSize.height = 1024;
        this.scene.add(dirLight);

        this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
        this.renderer.setSize(this.sceneWidth, this.sceneHeight);
        this.renderer.setAnimationLoop(this.animation);
        this.renderer.setPixelRatio(window.devicePixelRatio);
        this.renderer.shadowMap.enabled = true;
        this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
        this.renderer.setClearColor(0x000000, 0);

        // Enable XR. The button and session listeners are now handled externally in init.js
        this.renderer.xr.enabled = true;

        this.gameContainer.appendChild(this.renderer.domElement);

        // Setup internal logic
        this.bindDesktopControls();
        this.setupXrControls();
        this.create3dUi();
        this.create3dScore(); // NEW

        this.gameMover = new GameMover({
            scene: this.scene,
            gameGroup: this.gameGroup,
            controllers: this.controllers,
            camera: this.camera,
            moveSensitivity: 5, // <-- EXAMPLE: Game will move 50% further than your hand
            moverStartPosition: this.config.ar.moverStartPosition,
            moverStartScale: this.config.ar.moverStartScale
        });

        window.addEventListener('resize', this.onResize);
    }

    // This method is now called from the external listener in init.js
    onEnterXR = () => {
        this.ui2d.container.style.display = 'none';

        // --- Apply VR/AR positioning and scaling ---
        const { x, y, z } = this.config.ar.startPosition;
        this.gameGroup.position.set(x, y, z);
        this.gameGroup.rotation.set(0, 0, 0);

        // Apply the scale from the VR config to give a "tabletop" feel
        const vrScale = this.config.vr.scale;
        this.gameGroup.scale.set(vrScale, vrScale, vrScale);

        const session = this.renderer.xr.getSession();
        // Check if passthrough is supported
        const passthroughSupported = session.environmentBlendMode === 'alpha-blend' || session.environmentBlendMode === 'additive';

        this.isPassthroughActive = true; // Default to passthrough ON in AR/VR
        this.togglePassthrough(true); // Force update UI and visibility

        this.passthroughButton.visible = passthroughSupported;
        this.sfxButton.visible = true;
        this.mainActionButton.visible = true;
        this.uiGroup.visible = true;
        if (this.score3d) this.score3d.mesh.visible = true; // NEW

        if (this.gameMover) {
            this.gameMover.activate();
        }

            this.restart();
        }

        // This method is now called from the external listener in init.js
        onExitXR = () => {
            this.ui2d.container.style.display = 'flex';

            // --- Reset position and scale for desktop mode ---
            this.gameGroup.position.set(0, 0, 0);
            this.gameGroup.scale.set(1, 1, 1); // Reset scale to normal

            this.passthroughButton.visible = false;
            this.sfxButton.visible = false;
            this.mainActionButton.visible = false;
            this.uiGroup.visible = false;
            if (this.score3d) this.score3d.mesh.visible = false; // NEW
            this.starParticleSystem.visible = true;

            this.camera.position.set(0, 4, 5);
            this.camera.lookAt(0, 0, -5);

            if (this.gameMover) {
                this.gameMover.deactivate();
            }
            this.restart();
        }

        start = () => {
            this.restart();
            this.gameEnded = false;
            this.startGame();

            if (this.renderer.xr.isPresenting) {
                this.mainActionButton.visible = false;
            } else {
                this.ui2d.start.style.display = 'none';
                this.ui2d.restart.style.display = 'none';
                this.ui2d.score.style.display = 'block';
            }
        }

        restart = () => {
            this.gameEnded = true;
            this.lastTime = 0
            this.boxCounter = 0
            this.jumpCounter = 0;
            this.bonusCounter = 0;
            this.mouseDown = false;
            this.resetInput();

            if (this.playerBall) this.playerBall.visible = true;

            // --- Refactored to remove from gameGroup ---
            const childrenToRemove = this.gameGroup.children.filter(c => c.type === "Mesh" || c.isGroup);
            for (let i = childrenToRemove.length - 1; i >= 0; i--) {
                // Don't remove the UI, the score, or the control plane
                if (childrenToRemove[i] !== this.uiGroup && childrenToRemove[i] !== this.vrControlPlane && (!this.score3d || childrenToRemove[i] !== this.score3d.mesh)) { // MODIFIED
                    this.gameGroup.remove(childrenToRemove[i]);
                }
            }

            this.stack = [];

            this.spawnPlayerBall();
            this.spawnPlatforms();

            this.updateScore(0);

            if (this.renderer.xr.isPresenting) {
                this.mainActionButton.visible = true;
                const iconPath = this.config.ui.iconPaths.play;
                this.mainActionButton.userData.iconMaterial.map = this.textureLoader.load(iconPath);
            } else {
                this.ui2d.start.style.display = 'block';
                this.ui2d.restart.style.display = 'none';
                this.ui2d.score.style.display = 'none';
            }
        }

        bindDesktopControls = () => {
            this.ui2d.start.addEventListener('click', this.start);
            this.ui2d.restart.addEventListener('click', this.start);
            document.addEventListener('mousedown', this.updateMouseDown, false);
            document.addEventListener('mouseup', this.updateMouseUp, false);
            document.addEventListener('touchstart', this.updateTouch, false);
            document.addEventListener('touchend', this.updateMouseUp, false);
            document.addEventListener('mousemove', this.update, false);
            document.addEventListener('touchmove', this.updateTouch, false);
        }

        // Mouse/Touch input handlers
        updateTouch = (ev) => {
            var touch = ev.targetTouches[0];
            this.update(touch);
            this.mouseDown = true;
        }
        update = (ev) => {
            if (this.mouseDown) {
                this.screemInputLastPosX = this.screenInputPosX;
                this.screenInputPosX = ev.pageX / this.sceneWidth;
                this.screenInputPosX = this.screenInputPosX * 2 - 1;
                this.screenInputDeltaX = this.screemInputLastPosX - this.screenInputPosX;
            } else {
                this.resetInput();
            }
        }
        updateMouseDown = (ev) => {
            this.update(ev);
            this.mouseDown = true;
        }
        updateMouseUp = (ev) => {
            this.mouseDown = false;
            this.resetInput();
        }
        resetInput = () => {
            this.screenInputDeltaX = 0;
            this.screemInputLastPosX = undefined;
            this.screenInputPosX = undefined;
        }

        // --- XR Controller Setup ---
        setupXrControls = () => {
            this.raycaster = new THREE.Raycaster();

            for (let i = 0; i < 2; i++) {
                const controller = this.renderer.xr.getController(i);
                controller.addEventListener('selectstart', () => this.handleControllerSelect(controller));
                this.scene.add(controller);

                const pointerGeometry = new THREE.BufferGeometry().setFromPoints([new THREE.Vector3(0, 0, 0), new THREE.Vector3(0, 0, -1)]);
                const pointer = new THREE.Line(pointerGeometry);
                pointer.name = 'pointer';
                pointer.scale.z = 5;
                controller.add(pointer);
                this.controllers.push(controller);
            }
        }

        // --- Handle Controller Button Presses ---
        handleControllerSelect = (controller) => {
            const intersections = this.getIntersections(controller);

            if (intersections.length > 0) {
                const button = intersections[0].object.parent; // Button is the group
                this.animateButtonClick(button);

                if (button === this.passthroughButton) {
                    this.togglePassthrough();
                    return;
                }
                if (button === this.sfxButton) {
                    this.toggleSfxMute();
                    return;
                }
                if (this.gameEnded && button === this.mainActionButton) {
                    this.start();
                    return;
                }
            }
        }

        // --- 3D UI Creation ---
        create3dUi = () => {
            this.uiGroup = new THREE.Group();
            this.uiGroup.position.set(0, 0.8, -0.5); // Position UI relative to the game group
            this.gameGroup.add(this.uiGroup);

            this.mainActionButton = this.createUiButton(this.config.ui.iconPaths.play, 1.5);
            this.mainActionButton.position.y = 1;
            this.uiGroup.add(this.mainActionButton);

            this.passthroughButton = this.createUiButton(this.config.ui.iconPaths.passthroughShow);
            this.passthroughButton.position.set(3, -1, 1);
            this.uiGroup.add(this.passthroughButton);

            this.sfxButton = this.createUiButton(this.config.ui.iconPaths.sfxOn);
            this.sfxButton.position.set(-3, -1, 1);
            this.uiGroup.add(this.sfxButton);

            // Hide UI by default, will be shown on entering AR
            this.uiGroup.visible = false;
        }

        // NEW: Create the 3D score display
        create3dScore = () => {
            const canvas = document.createElement('canvas');
            const canvasWidth = 512;
            const canvasHeight = 128;
            canvas.width = canvasWidth;
            canvas.height = canvasHeight;
            const context = canvas.getContext('2d');

            const texture = new THREE.CanvasTexture(canvas);
            const material = new THREE.MeshBasicMaterial({
                map: texture,
                transparent: true,
                opacity: 1,
            });

            // Match plane dimensions to canvas aspect ratio for correct display
            const planeHeight = 1;
            const planeWidth = planeHeight * (canvasWidth / canvasHeight); // 4:1 ratio

            const geometry = new THREE.PlaneGeometry(planeWidth, planeHeight);
            const mesh = new THREE.Mesh(geometry, material);

            // Position it just below the first step (which is at y=0, z=0)
            mesh.position.set(0, 4, -10);
            mesh.raycast = () => { };

            mesh.visible = false; // Only visible in XR mode

            this.gameGroup.add(mesh);

            this.score3d = { canvas, context, mesh };
        }


        createUiButton = (iconPath, size = this.config.ui.buttonSize) => {
            const button = new THREE.Group();
            const background = new THREE.Mesh(
                new THREE.CircleGeometry(size / 2, 32),
                new THREE.MeshBasicMaterial({ color: 0x000000, transparent: true, opacity: 0.5 })
            );
            button.add(background);

            const iconMaterial = new THREE.MeshBasicMaterial({
                map: this.textureLoader.load(iconPath),
                transparent: true,
            });
            const icon = new THREE.Mesh(new THREE.PlaneGeometry(size * 0.7, size * 0.7), iconMaterial);
            icon.position.z = 0.01;
            button.add(icon);
            button.userData.iconMaterial = iconMaterial;
            return button;
        }

        animateButtonClick = (button) => {
            this.playSound('click');
            gsap.timeline()
                .to(button.scale, { x: 0.8, y: 0.8, z: 0.8, duration: 0.1 })
                .to(button.scale, { x: 1, y: 1, z: 1, duration: 0.1 });
        }

        togglePassthrough = (forceState) => {
            this.isPassthroughActive = forceState !== undefined ? forceState : !this.isPassthroughActive;
            this.starParticleSystem.visible = !this.isPassthroughActive;
            this.scene.background = this.isPassthroughActive ? null : new THREE.Color(0x000000);

            const iconPath = this.isPassthroughActive ? this.config.ui.iconPaths.passthroughHide : this.config.ui.iconPaths.passthroughShow;
            this.passthroughButton.userData.iconMaterial.map = this.textureLoader.load(iconPath);
        }

        toggleSfxMute = () => {
            this.isSfxMuted = !this.isSfxMuted;
            this.audioListener.setMasterVolume(this.isSfxMuted ? 0 : 1);
            const newIconPath = this.isSfxMuted ? this.config.ui.iconPaths.sfxOff : this.config.ui.iconPaths.sfxOn;
            this.sfxButton.userData.iconMaterial.map = this.textureLoader.load(newIconPath);
        }

        getIntersections = (controller) => {
            this.raycaster.setFromXRController(controller);
            return this.raycaster.intersectObjects(this.uiGroup.children, true);
        }

        // --- Game Logic ---
        getScore = () => {
            return this.jumpCounter * 100 + this.bonusCounter * 100;
        }

        onResize = () => {
            const { clientWidth, clientHeight } = this.gameContainer;
            this.renderer.setSize(clientWidth, clientHeight);
            if (this.camera.isPerspectiveCamera) {
                this.camera.aspect = clientWidth / clientHeight;
            }
            this.camera.updateProjectionMatrix();
        }

        spawnPlayerBall = () => {
            const sphereGeometry = new THREE.DodecahedronGeometry(this.playerRadius, 1);
            const sphereMaterial = new THREE.MeshStandardMaterial({ color: 0xe5f2f2, flatShading: true });
            this.playerBall = new THREE.Mesh(sphereGeometry, sphereMaterial);
            this.playerBall.castShadow = true;
            this.playerBall.position.y = this.boxHeight / 2;
            this.playerBall.position.x = 0;
            this.gameGroup.add(this.playerBall);
        }

        spawnPlatforms = () => {
            for (let i = 0; i < 12; i++) {
                this.generateBox(0, -this.boxHeight + i * this.boxHeight, -i * this.boxDepth, this.boxWidth, this.boxHeight, this.boxDepth);
                if (i > 2) {
                    this.generateObstaclesOnPlatform(this.stack[i]);
                }
            }
        }

        onPlayerLand = () => {
            const playerBoundsSkinWidth = this.playerRadius * 0.1;

            if (this.playerBall.position.x - playerBoundsSkinWidth > this.boxWidth / 2 || this.playerBall.position.x + playerBoundsSkinWidth < -this.boxWidth / 2) {
                this.onPlayerKill();
                return;
            }

            this.playSound('land');
            this.jumpCounter++;
            const currentPlatform = this.stack[this.jumpCounter % this.stack.length];
            const obstacles = currentPlatform.children;

            for (let i = 0; i < obstacles.length; i++) {
                const obstacle = obstacles[i];
                const obstaclePos = obstacle.position;
                const playerSkinWidth = this.playerRadius * 0.5;

                if (this.playerBall.position.x + playerSkinWidth > obstaclePos.x - this.obstacleWidth / 2 &&
                    this.playerBall.position.x - playerSkinWidth < obstaclePos.x + this.obstacleWidth / 2) {
                    if (obstacle.name === "Bonus") {
                        this.playSound('bonus');
                        gsap.to(obstacle.scale, { duration: .4, x: 0, y: 0, z: 0, ease: "power2.out" });
                        this.bonusCounter++;
                    } else {
                        this.onPlayerKill();
                        return;
                    }
                }
            }

            this.updateScore(this.getScore());
        }

        onPlayerKill = () => {
            if (this.gameEnded) return;
            this.gameEnded = true;
            this.stopPlayerJump();
            this.playerBall.visible = false;
            this.explodePlayerParticles();
            this.playSound('explode');

            const finalScore = this.getScore();
            setTimeout(() => {
                if (this.renderer.xr.isPresenting) {
                    this.mainActionButton.visible = true;
                    const iconPath = this.config.ui.iconPaths.restart;
                    this.mainActionButton.userData.iconMaterial.map = this.textureLoader.load(iconPath);
                    if (this.leaderboard) this.leaderboard.submit(finalScore);
                } else {
                    this.ui2d.score.style.display = 'none';
                    if (this.leaderboard && finalScore > 0) {
                        this.leaderboard.submitAndShow(finalScore, () => {
                            this.ui2d.restart.click();
                        });
                    } else {
                        this.ui2d.restart.style.display = 'block';
                    }
                }
            }, this.endGameDelayMs);
        }

        explodePlayerParticles = () => {
            this.isExploding = true;
            this.resetExplodeParticles();
        }

        resetExplodeParticles = () => {
            this.explosionParticleSystem.visible = true;
            const positions = this.explosionGeometry.attributes.position.array;
            for (let i = 0; i < this.explosionParticlesCount * 3; i += 3) {
                positions[i] = this.playerBall.position.x;
                positions[i + 1] = this.playerBall.position.y;
                positions[i + 2] = this.playerBall.position.z;
            }
            this.explosionGeometry.attributes.position.needsUpdate = true;
        }

        startPlayerJump = () => {
            this.playerBallTimeline = gsap.timeline({ repeat: -1, repeatDelay: 0 });
            this.playerBallTimeline.to(this.playerBall.position, {
                duration: (1 / this.gameSpeed) * 0.5,
                y: 1.5,
                ease: "power2.out"
            });
            this.playerBallTimeline.to(this.playerBall.position, {
                duration: (1 / this.gameSpeed) * 0.5,
                y: this.boxHeight / 2,
                ease: "power2.in",
                onComplete: this.onPlayerLand
            });
        }

        stopPlayerJump = () => {
            if (this.playerBallTimeline) {
                this.playerBallTimeline.kill();
                this.playerBallTimeline = null;
            }
        }

        startGame = () => {
            this.lastTime = 0;
            this.gameSpeed = 1.7;
            this.resetInput();
            this.stopPlayerJump();
            this.startPlayerJump();
            this.gameEnded = false;
            this.updateScore(this.getScore());
        }

        generateBox = (x, y, z, width, height, depth) => {
            const geometry = new THREE.BoxGeometry(width, height, depth);
            const color = new THREE.Color(`hsl(${30 + this.stack.length * 4}, 100%, 50%)`);
            const material = new THREE.MeshStandardMaterial({ color, fog: true });
            const mesh = new THREE.Mesh(geometry, material);
            mesh.receiveShadow = true;
            mesh.position.set(x, y, z);
            this.boxCounter++;
            this.gameGroup.add(mesh);
            this.stack.push(mesh);
        }

        animateStars = (time) => {
            if (!this.starParticleSystem.visible) return;
            const timeFactor = time * 0.0005;
            const sizes = this.starGeometry.attributes.size.array;
            for (let i = 0; i < this.starParticlesCount; i++) {
                sizes[i] = 0.03 * (1 + Math.sin(0.1 * i + timeFactor));
            }
            this.starGeometry.attributes.size.needsUpdate = true;
        }

        respawnBox = (platform) => {
            // --- FIX: Find the platform that is currently at the very back ---
            // We'll use this as the anchor for the new position.
            let lastPlatform = this.stack[0];
            for (let i = 1; i < this.stack.length; i++) {
                // Find the platform with the smallest (most negative) Z position.
                if (this.stack[i].position.z < lastPlatform.position.z) {
                    lastPlatform = this.stack[i];
                }
            }

            // --- FIX: Set the new position RELATIVE to the last platform ---
            // This ensures perfect spacing every time, regardless of floating point drift or timing.
            const newZ = lastPlatform.position.z - this.boxDepth;
            const newY = lastPlatform.position.y + this.boxHeight;
            platform.position.set(0, newY, newZ);

            // The rest of the function remains the same
            const color = new THREE.Color(`hsl(${30 + this.boxCounter * 4}, 100%, 50%)`);
            platform.material.color = color;
            this.boxCounter++;
            this.clearObstaclesOnPlatform(platform);
            this.generateObstaclesOnPlatform(platform);
        }

        clearObstaclesOnPlatform = (platform) => {
            while (platform.children.length > 0) {
                const child = platform.children[0];
                platform.remove(child);
            }
        }

        generateObstaclesOnPlatform = (platform) => {
            const minX = -(this.boxWidth / 2 - this.obstacleWidth / 2);
            const maxObstacles = Math.floor(this.boxWidth / this.obstacleWidth);
            const numOfObstacles = Math.min(Math.floor(this.jumpCounter / 20) + 2, maxObstacles - 1);
            const obstaclePositions = this.getRandomObstaclePositions(maxObstacles, numOfObstacles);

            for (let i = 0; i < obstaclePositions.length - 1; i++) {
                const positionIndex = obstaclePositions[i] - 1;
                const obstacle = this.generateObstacle(minX + this.obstacleWidth * positionIndex);
                platform.add(obstacle);
            }

            const bonusPosIndex = obstaclePositions[obstaclePositions.length - 1] - 1;
            const bonus = this.generateBonus(minX + this.obstacleWidth * bonusPosIndex);
            platform.add(bonus);
        }

        getRandomObstaclePositions = (maxObstacles, numOfObstacles) => {
            const numbers = Array(maxObstacles).fill().map((_, index) => index + 1);
            numbers.sort(() => Math.random() - 0.5);
            return numbers.slice(0, numOfObstacles);
        }

        generateBonus = (x) => {
            const geometry = new THREE.CircleGeometry(this.obstacleWidth * 0.6, 32);
            const material = new THREE.MeshBasicMaterial({ map: this.bonusTex, transparent: true, blending: THREE.AdditiveBlending });
            const mesh = new THREE.Mesh(geometry, material);
            mesh.name = "Bonus";
            mesh.position.set(x, 0.21, 0);
            mesh.rotation.x = -Math.PI / 2;
            return mesh;
        }

        generateObstacle = (x) => {
            const geometry = new THREE.ConeGeometry(this.obstacleWidth / 2, this.obstacleHeight, 32);
            const material = new THREE.MeshStandardMaterial({ color: new THREE.Color(0.5, 0.5, 0.5) });
            const mesh = new THREE.Mesh(geometry, material);
            mesh.castShadow = true;
            mesh.position.set(x, this.obstacleHeight / 2, 0);
            return mesh;
        }

        playSound = (name) => {
            if (this.isSfxMuted) return;
            // console.log(`Playing sound: ${name}`);
        }

        updateControllerInteractions = () => {
            if (!this.renderer.xr.isPresenting) return;

            const intersectingButtons = new Set();
            let isHoveringMover = false; // Flag for the grab handle

            for (const controller of this.controllers) {
                // We only need to cast once per controller
                this.raycaster.setFromXRController(controller);

                // Check for UI button intersections
                const uiIntersections = this.raycaster.intersectObjects(this.uiGroup.children, true);
                if (uiIntersections.length > 0) {
                    intersectingButtons.add(uiIntersections[0].object.parent);
                }

                // --- NEW: Check for GameMover handle intersection ---
                if (this.gameMover?.grabHandle) {
                    const moverIntersections = this.raycaster.intersectObject(this.gameMover.grabHandle);
                    if (moverIntersections.length > 0) {
                        isHoveringMover = true;
                    }
                }
            }

            // --- NEW: Inform the GameMover of its hover state ---
            if (this.gameMover) {
                this.gameMover.setHoverState(isHoveringMover);
            }

            // Animate UI buttons based on hover
            [this.mainActionButton, this.passthroughButton, this.sfxButton].forEach(button => {
                if (!button) return;
                const isHit = intersectingButtons.has(button);
                gsap.to(button.scale, { x: isHit ? 1.2 : 1, y: isHit ? 1.2 : 1, z: 1, duration: 0.2 });
            });
        }

        animation = (time) => {
            gsap.ticker.tick();
            const timePassed = this.lastTime ? time - this.lastTime : 0;
            const deltaTime = timePassed / 1000;

            if (this.gameMover) {
                this.gameMover.update();
            }

            this.updateControllerInteractions();

            if (!this.gameEnded) {
                this.gameSpeed += this.gameSpeedIncrease * deltaTime;
                for (let i = 0; i < this.stack.length; i++) {
                    this.stack[i].position.z += this.boxDepth * deltaTime * this.gameSpeed;
                    this.stack[i].position.y -= this.boxHeight * deltaTime * this.gameSpeed;
                }
                for (let i = 0; i < this.stack.length; i++) {
                    if (this.stack[i].position.z > this.boxDepth * 3) {
                        this.respawnBox(this.stack[i]);
                    }
                }

                this.playerBall.rotation.x -= 0.006 * timePassed;
                if (this.playerBallTimeline) this.playerBallTimeline.duration(1 / this.gameSpeed);

                // --- UPDATED: VR Pointer Controls ---
                if (this.renderer.xr.isPresenting) {
                    const controller = this.controllers[1]; // Use the main (right) controller
                    if (controller) {
                        // Set the raycaster from the controller's current pose
                        this.raycaster.setFromXRController(controller);

                        // Check for an intersection with our invisible control plane
                        const intersects = this.raycaster.intersectObject(this.vrControlPlane);

                        if (intersects.length > 0) {
                            // The intersection point is in world coordinates.
                            // We convert it to the local coordinates of the gameGroup to get the correct X value.
                            const localPoint = this.gameGroup.worldToLocal(intersects[0].point);
                            let targetX = localPoint.x;

                            // Clamp the position to keep the ball on the platforms
                            const halfWidth = this.boxWidth / 2 - this.playerRadius;
                            targetX = THREE.MathUtils.clamp(targetX, -halfWidth, halfWidth);

                            // Smoothly move the player ball towards the target X position
                            this.playerBall.position.x += (targetX - this.playerBall.position.x) * 0.2;
                        }
                    }
                } else { // --- Original Desktop/Mobile Controls ---
                    if (this.screenInputDeltaX !== undefined && !isNaN(this.screenInputDeltaX)) {
                        this.playerBall.position.x -= this.screenInputDeltaX * this.inputMultiplier;
                    }
                    this.screenInputDeltaX = 0;
                }
            }

            this.renderer.render(this.scene, this.camera);
            this.animateStars(time);
            this.updateExplosionParticles();
            this.lastTime = time;
        }

        updateExplosionParticles = () => {
            if (this.isExploding) {
                const positions = this.explosionGeometry.attributes.position.array;
                let stillVisible = false;
                for (let i = 0; i < this.explosionParticlesCount; i++) {
                    const index = i * 3;
                    positions[index] += this.explosionParticlesDirs[i].x * this.movementSpeed;
                    positions[index + 1] += this.explosionParticlesDirs[i].y * this.movementSpeed;
                    positions[index + 2] += this.explosionParticlesDirs[i].z * this.movementSpeed;
                    if (positions[index + 1] > -2) stillVisible = true;
                }
                this.explosionGeometry.attributes.position.needsUpdate = true;
                if (!stillVisible) {
                    this.isExploding = false;
                    this.explosionParticleSystem.visible = false;
                }
            }
        }

        createParticleSystem(config, texture) {
            const vertexShader = `attribute float size; varying vec3 vColor; void main() { vColor = color; vec4 mvPosition = modelViewMatrix * vec4( position, 1.0 ); gl_PointSize = size * ( 300.0 / -mvPosition.z ); gl_Position = projectionMatrix * mvPosition; }`;
            const fragmentShader = `uniform sampler2D pointTexture; varying vec3 vColor; void main() { gl_FragColor = vec4( vColor, 1.0 ); gl_FragColor = gl_FragColor * texture2D( pointTexture, gl_PointCoord ); }`;
            const uniforms = { pointTexture: { value: texture } };
            const shaderMaterial = new THREE.ShaderMaterial({ uniforms, vertexShader, fragmentShader, blending: THREE.AdditiveBlending, depthTest: true, depthWrite: false, transparent: true, vertexColors: true });

            // The rest of the function remains the same...
            const geometry = new THREE.BufferGeometry();
            const positions = []; const colors = []; const sizes = [];

            for (let i = 0; i < config.particleCount; i++) {
                const pos = config.position ? config.position() : new THREE.Vector3((Math.random() * 2 - 1), (Math.random() * 2 - 1), (Math.random() * 2 - 1));
                positions.push(pos.x, pos.y, pos.z);
                const color = typeof config.color === 'function' ? config.color(i, config.particleCount) : config.color;
                colors.push(color.r, color.g, color.b);
                sizes.push(config.size());
                if (config.particleInit) {
                    const dir = new THREE.Vector3((Math.random() * 2 - 1), (Math.random() * 2 - 1), (Math.random() * 2 - 1)).normalize();
                    config.particleInit(pos, dir);
                }
            }
            geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3).setUsage(THREE.DynamicDrawUsage));
            geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
            geometry.setAttribute('size', new THREE.Float32BufferAttribute(sizes, 1).setUsage(THREE.DynamicDrawUsage));

            return new THREE.Points(geometry, shaderMaterial);
        }

        // MODIFIED: This now updates both the 2D and 3D score displays.
        updateScore = (score) => {
            // 1. Update 2D HTML score
            this.ui2d.score.textContent = `Score: ${score}`;

            // 2. Update 3D canvas score
            if (this.score3d && this.score3d.context) {
                const { context, canvas, mesh } = this.score3d;
                const text = `Score: ${score}`;

                // Clear the canvas before drawing new text
                context.clearRect(0, 0, canvas.width, canvas.height);

                // Style and draw the text
                context.font = 'bold 60px Arial';
                context.fillStyle = 'white';
                context.textAlign = 'center';
                context.textBaseline = 'middle';
                context.fillText(text, canvas.width / 2, canvas.height / 2);

                // Tell three.js that the texture has changed and needs to be re-uploaded to the GPU
                if (mesh.material.map) {
                    mesh.material.map.needsUpdate = true;
                }
            }
        }

    dispose = () => {
        console.log("DISPOSE")
        this.renderer.setAnimationLoop(null);
        window.removeEventListener('resize', this.onResize);
        document.removeEventListener('mousedown', this.updateMouseDown);
        document.removeEventListener('mouseup', this.updateMouseUp);
        document.removeEventListener('touchstart', this.updateTouch);
        document.removeEventListener('touchend', this.updateMouseUp);
        document.removeEventListener('mousemove', this.update);
        document.removeEventListener('touchmove', this.updateTouch);

        if (this.playerBallTimeline) {
            this.playerBallTimeline.kill();
        }

        // Dispose of Three.js objects
        this.scene.traverse(object => {
            if (object.isMesh) {
                if (object.geometry) {
                    object.geometry.dispose();
                }
                if (object.material) {
                    if (Array.isArray(object.material)) {
                        object.material.forEach(material => material.dispose());
                    } else {
                        object.material.dispose();
                    }
                }
            }
        });
        this.renderer.dispose();
    }
}

export default Stairs;